/*!
 * AudioLogStore — where the segments of a long recording live until the person
 * has them on their own device.
 *
 * Why this exists. /audio-logger kept each segment in the tab's heap and
 * handed it over with one blob-anchor click when the segment closed. Three
 * things were measured against that on 13.10.2026:
 *
 *   • the browser gives a page ONE download per user interaction (__dlGesture
 *     in timbrica-core.js). A rotation is a timer, not an interaction — stand
 *     tests/playwright/audio-logger-keeps-every-segment.mjs: four segments
 *     recorded, the list said «saved» four times, two files existed. In an
 *     unattended night that is one hour on disk and the rest in memory;
 *   • the list was text. Field, iPhone, 05.10: three taps on the file name,
 *     then «nichts zum öffnen» — and the blob URL was revoked two minutes after
 *     the click, so a dismissed download prompt cost the whole take;
 *   • a page that goes away mid-recording (iOS asks nobody before it leaves)
 *     took the running segment with it.
 *
 * So every chunk the recorder emits is written here the moment it arrives, a
 * closed segment stays here until the person removes it, and the list is built
 * from what is stored — not from what was clicked.
 *
 * Storage. IndexedDB opened through window.__workStorage (the persisted
 * `work` bucket where the browser has buckets, strict durability): a chunk
 * per record, the layout RecordingGuard chose by bench (rewriting one growing
 * blob costs O(n²) bytes). A Blob read back from the records is a list of
 * references — an hour-long segment is not copied into memory to be played or
 * saved. Where IndexedDB cannot be opened (private windows on some engines)
 * the store runs in memory: nothing survives the page, the tool says so.
 *
 * A chunk whose write fails (quota) is kept in memory for the life of the page
 * and the segment is marked volatile — the bytes are still handed over, they
 * just would not survive a reload.
 *
 * Liveness. A recording page holds a Web Lock named after its session for as
 * long as it lives; the browser releases it however the page dies. A segment
 * left in state «recording» whose session holds no lock belongs to a dead page
 * and is adopted as an interrupted take. Without the Locks API the last chunk
 * time decides (STALE_MS).
 *
 * window.AudioLogStore
 *   open(slug)                    → Promise<store>
 *   store.mode                    'b' bucket | 'd' default IndexedDB | 'm' memory
 *   store.session                 this page's session id
 *   store.begin(meta)             → segment {id, meta, push(blob), end(patch) → Promise<meta>, bytes(), chunks()}
 *   store.list()                  → Promise<meta[]> oldest first (adopts dead pages' segments)
 *   store.blob(id)                → Promise<Blob|null>
 *   store.patch(id, patch)        → Promise<meta|null>
 *   store.replace(id, blob, patch)→ Promise<meta|null>   one record instead of the chunks
 *   store.remove(id)              → Promise
 *   store.usage()                 → Promise<{bytes, count}>
 * Guard: tests/js/audio-log-store.test.mjs.
 */
(function (global) {
'use strict';
if (global.AudioLogStore) return;

var DB = 'timbrica_audio_log', VER = 1, SEG = 'segments', CH = 'chunks';
var LOCK = 'tb-audio-log:';
var STALE_MS = 45000;          // no Locks API: a «recording» segment silent this long is a dead page's
var META_EVERY = 5;            // meta is refreshed every N chunks — the chunks are the payload
var WHOLE = -1;                // chunk index of a closed segment rewritten as one record

function uid() {
    return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}
function req(r) {
    return new Promise(function (res, rej) {
        r.onsuccess = function () { res(r.result); };
        r.onerror = function () { rej(r.error || new Error('idb')); };
    });
}
function done(t) {
    return new Promise(function (res, rej) {
        t.oncomplete = function () { res(); };
        t.onerror = t.onabort = function () { rej(t.error || new Error('idb-tx')); };
    });
}
function locksApi() {
    try { return !!(global.navigator && navigator.locks && typeof navigator.locks.request === 'function' && typeof navigator.locks.query === 'function'); }
    catch (_) { return false; }
}
function tel(f, data) {
    try { if (typeof global.__ae === 'function') global.__ae('feature_use', Object.assign({ f: f }, data || {})); } catch (_) {}
}

function upgrade(db) {
    if (!db.objectStoreNames.contains(SEG)) db.createObjectStore(SEG, { keyPath: 'id' });
    if (!db.objectStoreNames.contains(CH)) db.createObjectStore(CH);
}

function openDb() {
    var ws = global.__workStorage;
    if (ws && typeof ws.open === 'function') {
        return ws.open(DB, VER, upgrade, { stamp: function (v) { return (v && v.updatedAt) || 0; } })
            .then(function (o) { return { db: o.db, mode: o.mode }; });
    }
    var idb = global.indexedDB;
    if (!idb) return Promise.reject(new Error('no-idb'));
    return new Promise(function (res, rej) {
        var r;
        try { r = idb.open(DB, VER); } catch (e) { rej(e); return; }
        r.onupgradeneeded = function () { upgrade(r.result); };
        r.onsuccess = function () { res({ db: r.result, mode: 'd' }); };
        r.onerror = function () { rej(r.error || new Error('idb-open')); };
        r.onblocked = function () { rej(new Error('idb-blocked')); };
    });
}

function Store(slug, db, mode) {
    this.slug = slug;
    this.mode = mode;
    this.session = uid();
    this._db = db;
    this._mem = { segs: {}, chunks: {} };   // memory mode, and the volatile tail of a failed write
    this._locked = false;
}

Store.prototype._tx = function (stores, write) {
    var ws = global.__workStorage;
    if (write && ws && typeof ws.tx === 'function') return ws.tx(this._db, stores);
    return this._db.transaction(stores, write ? 'readwrite' : 'readonly');
};

/* The lock is taken when the first segment begins, not when the page opens:
   a held lock keeps a page out of the back/forward cache. */
Store.prototype._hold = function () {
    if (this._locked || !locksApi()) return;
    this._locked = true;
    try {
        var p = navigator.locks.request(LOCK + this.session, function () { return new Promise(function () {}); });
        if (p && p.catch) p.catch(function () {});
    } catch (_) { this._locked = false; }
};

Store.prototype._putMeta = function (meta) {
    meta.updatedAt = Date.now();
    if (this.mode === 'm') { this._mem.segs[meta.id] = Object.assign({}, meta); return Promise.resolve(meta); }
    var t = this._tx([SEG], true);
    t.objectStore(SEG).put(meta);
    return done(t).then(function () { return meta; });
};

Store.prototype._getMeta = function (id) {
    if (this.mode === 'm') return Promise.resolve(this._mem.segs[id] ? Object.assign({}, this._mem.segs[id]) : null);
    return req(this._tx([SEG]).objectStore(SEG).get(id)).then(function (m) { return m || null; });
};

Store.prototype.begin = function (meta) {
    var self = this;
    this._hold();
    var now = Date.now();
    var m = Object.assign({}, meta || {}, {
        id: uid(), slug: this.slug, session: this.session, state: 'recording',
        startedAt: (meta && meta.startedAt) || now, endedAt: null, bytes: 0, chunks: 0,
        saved: null, volatile: this.mode === 'm' ? 1 : 0
    });
    var idx = 0, bytes = 0, chain = this._putMeta(m).catch(function () {});
    var seg = {
        id: m.id,
        meta: m,
        bytes: function () { return bytes; },
        chunks: function () { return idx; },
        push: function (blob) {
            if (!blob || !blob.size) return chain;
            var i = idx++;
            bytes += blob.size;
            chain = chain.then(function () {
                if (self.mode === 'm') { (self._mem.chunks[m.id] = self._mem.chunks[m.id] || [])[i] = blob; return; }
                var t = self._tx(i % META_EVERY === 0 ? [CH, SEG] : [CH], true);
                t.objectStore(CH).put(blob, [m.id, i]);
                if (i % META_EVERY === 0) {
                    m.bytes = bytes; m.chunks = idx; m.updatedAt = Date.now();
                    t.objectStore(SEG).put(m);
                }
                return done(t);
            }).catch(function (e) {
                /* The write failed — the bytes stay with the page. */
                (self._mem.chunks[m.id] = self._mem.chunks[m.id] || [])[i] = blob;
                if (!m.volatile) {
                    m.volatile = 1;
                    tel('al_store_write_failed', { slug: self.slug, err: String((e && e.name) || e).slice(0, 40), mode: self.mode });
                }
            });
            return chain;
        },
        end: function (patch) {
            chain = chain.then(function () {
                Object.assign(m, patch || {}, { state: 'kept', endedAt: (patch && patch.endedAt) || Date.now(), bytes: bytes, chunks: idx });
                return self._putMeta(m).catch(function () { self._mem.segs[m.id] = Object.assign({}, m); return m; });
            });
            return chain.then(function () { return Object.assign({}, m); });
        }
    };
    return seg;
};

Store.prototype._alive = function () {
    if (!locksApi()) return Promise.resolve(null);
    return navigator.locks.query().then(function (q) {
        var held = {};
        (q.held || []).forEach(function (l) { if (l.name && l.name.indexOf(LOCK) === 0) held[l.name.slice(LOCK.length)] = 1; });
        return held;
    }).catch(function () { return null; });
};

/* Oldest first. A «recording» segment of a session that is gone is closed here
   as an interrupted take; an empty one is removed. */
Store.prototype.list = function () {
    var self = this;
    var all = this.mode === 'm'
        ? Promise.resolve(Object.keys(this._mem.segs).map(function (k) { return Object.assign({}, self._mem.segs[k]); }))
        : req(this._tx([SEG]).objectStore(SEG).getAll());
    return Promise.all([all, this._alive()]).then(function (r) {
        var metas = (r[0] || []).filter(function (m) { return m && m.slug === self.slug; });
        var held = r[1], now = Date.now(), fixes = [];
        metas.forEach(function (m) {
            if (m.state !== 'recording' || m.session === self.session) return;
            var dead = held ? !held[m.session] : (now - (m.updatedAt || m.startedAt || 0) > STALE_MS);
            if (!dead) { m.elsewhere = 1; return; }
            fixes.push(self._count(m.id).then(function (c) {
                if (!c.chunks) { m._gone = 1; return self.remove(m.id); }
                m.state = 'kept'; m.interrupted = 1; m.bytes = c.bytes; m.chunks = c.chunks;
                m.endedAt = m.updatedAt || m.startedAt;
                /* The recorder cut a chunk a second, so the count is the length —
                   closer than the clock: meta is refreshed every few chunks, and
                   a pause by silence leaves time on the clock but no chunks. */
                if (!m.durMs) m.durMs = c.chunks * 1000;
                tel('al_segment_adopted', { slug: self.slug, kb: Math.round(c.bytes / 1024), chunks: c.chunks });
                return self._putMeta(m);
            }).catch(function () {}));
        });
        return Promise.all(fixes).then(function () {
            return metas.filter(function (m) { return !m._gone; })
                .sort(function (a, b) { return (a.startedAt || 0) - (b.startedAt || 0); });
        });
    });
};

Store.prototype._range = function (id, withWhole) {
    return global.IDBKeyRange.bound([id, withWhole ? WHOLE : 0], [id, Number.MAX_SAFE_INTEGER]);
};

Store.prototype._count = function (id) {
    return this._parts(id).then(function (parts) {
        var b = 0;
        parts.forEach(function (p) { b += p.size; });
        return { chunks: parts.length, bytes: b };
    });
};

/* The chunk Blobs in order — stored ones first, then whatever a failed write
   left with the page. */
Store.prototype._parts = function (id) {
    var tail = (this._mem.chunks[id] || []);
    if (this.mode === 'm') return Promise.resolve(tail.filter(Boolean));
    var self = this;
    return req(this._tx([CH]).objectStore(CH).get([id, WHOLE])).then(function (whole) {
        if (whole) return [whole];
        return self._chunks(id, tail);
    });
};

Store.prototype._chunks = function (id, tail) {
    var self = this;
    return req(this._tx([CH]).objectStore(CH).getAll(this._range(id))).then(function (stored) {
        /* A chunk that failed to write sits at its own index in the tail; the
           stored list has no hole for it, so the order is rebuilt by key. */
        if (!tail.length) return stored || [];
        return req(self._tx([CH]).objectStore(CH).getAllKeys(self._range(id))).then(function (keys) {
            var byIdx = [];
            (keys || []).forEach(function (k, n) { byIdx[k[1]] = stored[n]; });
            tail.forEach(function (b, n) { if (b) byIdx[n] = b; });
            return byIdx.filter(Boolean);
        });
    });
};

Store.prototype.blob = function (id) {
    var self = this;
    return Promise.all([this._getMeta(id), this._parts(id)]).then(function (r) {
        var m = r[0] || self._mem.segs[id], parts = r[1];
        if (!parts.length) return null;
        return new Blob(parts, { type: (m && m.mime) || 'application/octet-stream' });
    });
};

Store.prototype.patch = function (id, patch) {
    var self = this;
    return this._getMeta(id).then(function (m) {
        if (!m) return null;
        Object.assign(m, patch || {});
        return self._putMeta(m);
    });
};

/* One record in place of the chunks — after a segment was rewritten into its
   finished form (a WebM given its duration and index), and so that a closed
   segment opens from one read instead of thousands.

   In two steps, and the order is the point. The new Blob is BUILT FROM the
   chunk Blobs — a list of references into the very records it replaces. So it
   is first written beside them and read back; the chunks go only after the
   copy has answered with its full size. Deleting them in the same transaction
   as the put leaves it to each engine whether the sources still exist when the
   copy is made — and the engine this page was rebuilt for (WebKit) cannot be
   run here to find out. A copy that did not land whole is dropped and the
   chunks stay: the segment is then merely slower to open. */
Store.prototype.replace = function (id, blob, patch) {
    var self = this;
    if (this.mode === 'm') {
        this._mem.chunks[id] = [blob];
        return this.patch(id, Object.assign({ bytes: blob.size, chunks: 1 }, patch || {}));
    }
    return this._getMeta(id).then(function (m) {
        if (!m) return null;
        var t = self._tx([CH], true);
        t.objectStore(CH).put(blob, [id, WHOLE]);
        return done(t).then(function () {
            return req(self._tx([CH]).objectStore(CH).get([id, WHOLE]));
        }).then(function (back) {
            if (!back || back.size !== blob.size) {
                tel('al_store_replace_refused', { slug: self.slug, want: blob.size, got: back ? back.size : -1 });
                var undo = self._tx([CH], true);
                undo.objectStore(CH).delete([id, WHOLE]);
                return done(undo).then(function () { throw new Error('replace-verify'); });
            }
            var t2 = self._tx([CH, SEG], true);
            t2.objectStore(CH).delete(self._range(id));
            Object.assign(m, patch || {}, { bytes: blob.size, chunks: 1, updatedAt: Date.now() });
            t2.objectStore(SEG).put(m);
            return done(t2).then(function () { delete self._mem.chunks[id]; return m; });
        });
    });
};

Store.prototype.remove = function (id) {
    delete this._mem.chunks[id];
    delete this._mem.segs[id];
    if (this.mode === 'm') return Promise.resolve();
    var t = this._tx([CH, SEG], true);
    t.objectStore(CH).delete(this._range(id, true));
    t.objectStore(SEG).delete(id);
    return done(t).catch(function () {});
};

Store.prototype.usage = function () {
    return this.list().then(function (metas) {
        var b = 0;
        metas.forEach(function (m) { b += m.bytes || 0; });
        return { bytes: b, count: metas.length };
    });
};

function open(slug) {
    return openDb().then(function (o) {
        return new Store(slug, o.db, o.mode);
    }, function (e) {
        tel('al_store_memory', { slug: slug, err: String((e && e.message) || e).slice(0, 40) });
        return new Store(slug, null, 'm');
    });
}

global.AudioLogStore = { open: open, LOCK: LOCK, STALE_MS: STALE_MS };
})(typeof window !== 'undefined' ? window : this);
